import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, Send, Upload, X, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportBug = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bugId, setBugId] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  
  const [form, setForm] = useState({
    reporter_name: '',
    reporter_email: '',
    category: '',
    title: '',
    description: '',
    page_url: window.location.href,
    browser_info: navigator.userAgent
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bug-reports/categories`);
      setCategories(res.data);
      if (res.data.length > 0) {
        setForm(prev => ({ ...prev, category: res.data[0].value }));
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Fallback categories
      setCategories([
        { value: 'website_issue', label: 'Website Issue' },
        { value: 'checkout_problem', label: 'Checkout Problem' },
        { value: 'account_issue', label: 'Account Issue' },
        { value: 'other', label: 'Other' }
      ]);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Screenshot must be less than 5MB');
        return;
      }
      
      setScreenshot(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.reporter_name || !form.reporter_email || !form.title || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = { ...form };
      
      // Add screenshot if present
      if (screenshotPreview) {
        submitData.screenshot_data = screenshotPreview;
      }
      
      const res = await axios.post(`${API_URL}/api/bug-reports/report`, submitData);
      
      setBugId(res.data.bug_id);
      setSubmitted(true);
      toast.success('Bug report submitted successfully!');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-20">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h1>
            <p className="text-gray-600 mb-6">
              Your bug report has been submitted successfully. We'll investigate and get back to you soon.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500">Reference ID</p>
              <p className="font-mono font-medium text-[#003456]">{bugId?.slice(0, 8).toUpperCase()}</p>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              A confirmation email has been sent to your email address.
            </p>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate('/')}>
                Back to Home
              </Button>
              <Button onClick={() => { setSubmitted(false); setForm({ ...form, title: '', description: '' }); }}>
                Report Another Issue
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bug className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Report a Bug</h1>
          <p className="text-gray-600">
            Found something not working? Let us know and we'll fix it!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
          {/* Contact Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={form.reporter_name}
                onChange={(e) => setForm({ ...form, reporter_name: e.target.value })}
                placeholder="John Doe"
                required
                data-testid="bug-reporter-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                value={form.reporter_email}
                onChange={(e) => setForm({ ...form, reporter_email: e.target.value })}
                placeholder="john@example.com"
                required
                data-testid="bug-reporter-email"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent"
              required
              data-testid="bug-category-select"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Issue Title <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Brief description of the issue"
              required
              data-testid="bug-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Detailed Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Please describe what happened, what you expected, and steps to reproduce the issue..."
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent resize-none"
              required
              data-testid="bug-description-textarea"
            />
          </div>

          {/* Page URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Page URL (where the issue occurred)
            </label>
            <Input
              value={form.page_url}
              onChange={(e) => setForm({ ...form, page_url: e.target.value })}
              placeholder="https://..."
              data-testid="bug-url-input"
            />
          </div>

          {/* Screenshot Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Screenshot (optional)
            </label>
            {screenshotPreview ? (
              <div className="relative inline-block">
                <img 
                  src={screenshotPreview} 
                  alt="Screenshot preview" 
                  className="max-h-48 rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={removeScreenshot}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">Click to upload a screenshot</p>
                  <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  data-testid="bug-screenshot-input"
                />
              </label>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#003456] hover:bg-[#002B47] text-white py-3"
            data-testid="submit-bug-report-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Submit Bug Report
              </>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            By submitting, you agree to receive email updates about your report.
          </p>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default ReportBug;
